import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Calculator, Search, Menu, X } from "lucide-react";
import { useT } from "@/lib/i18n";
import { CALCULATORS } from "@/lib/calculators";
import { LanguageToggle } from "./LanguageToggle";
import { ThemeToggle } from "./ThemeToggle";
import { Sidebar } from "./Sidebar";

export function Header() {
  const { t, lang } = useT();
  const [query, setQuery] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const matches = query.trim()
    ? CALCULATORS.filter((c) =>
        c.name.en.toLowerCase().includes(query.toLowerCase()) ||
        c.name.bn.includes(query) ||
        c.slug.includes(query.toLowerCase()),
      ).slice(0, 6)
    : [];

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-3 sm:px-4 md:px-6">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Calculator size={18} />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-sm font-bold">{t("brand.appName")}</span>
              <span className="hidden text-[10px] text-muted-foreground sm:block">calculatorbangla.com</span>
            </span>
          </Link>

          {/* Search — hidden on mobile, shown from md up */}
          <div className="relative mx-auto hidden w-full max-w-md md:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <input
              type="text"
              placeholder={t("common.searchPlaceholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={`w-full rounded-lg border border-border bg-muted py-2 pl-9 pr-3 text-sm outline-none transition focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/20 ${lang === "bn" ? "font-bn" : ""}`}
            />
            {matches.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-2 overflow-hidden rounded-lg border border-border bg-popover shadow-lg">
                {matches.map((c) => (
                  <button
                    key={c.slug}
                    onClick={() => {
                      setQuery("");
                      navigate({ to: "/$slug", params: { slug: c.slug } });
                    }}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted"
                  >
                    <span className={lang === "bn" ? "font-bn" : ""}>{c.name[lang]}</span>
                    <span className="text-xs text-muted-foreground">/{c.slug}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Spacer to push controls right on mobile */}
          <div className="flex-1 md:hidden" />

          <div className="flex items-center gap-1.5 sm:gap-2">
            <LanguageToggle />
            <ThemeToggle />
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border"
              aria-label="Open menu"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer — slides in from the right */}
      <div
        className={`fixed inset-0 z-50 md:hidden ${mobileOpen ? "" : "pointer-events-none"}`}
        inert={!mobileOpen}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${
            mobileOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMobileOpen(false)}
        />
        <div
          className={`absolute inset-y-0 right-0 w-72 max-w-[85%] overflow-y-auto bg-sidebar p-4 shadow-xl transition-transform duration-200 ease-out ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="mb-4 flex items-center justify-between">
            <span className="font-bold">{t("common.categories")}</span>
            <button
              onClick={() => setMobileOpen(false)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          </div>
          <Sidebar onNavigate={() => setMobileOpen(false)} />
        </div>
      </div>
    </>
  );
}
